import React, { useState, useEffect } from 'react';
import { Helmet } from 'react-helmet-async';
import { CheckCircle2, Play, X } from 'lucide-react';
import { Link } from 'react-router-dom';
import TestimonialCarousel from '../components/TestimonialCarousel';

const SeHvordanJegArbejder = () => {
  const [videos, setVideos] = useState([]);
  const [loading, setLoading] = useState(true);
  const [activeVideo, setActiveVideo] = useState(null);

  useEffect(() => {
    const fetchVideos = async () => {
      try {
        const response = await fetch('https://api.dev.auxo.dk/public/content/videos');
        if (response.ok) {
          const data = await response.json();
          if (data && data.length > 0) {
            setVideos(data);
          } else {
            // Fallback to static video placeholders
            setVideos([
              {
                id: 'v1',
                title: { da: 'Klientens oplevelse af første behandling' },
                description: { da: 'Se en klients ærlige oplevelse af første behandling hos Corehypnose.' },
                thumbnail: ['https://assets.auxodev.dk/assets/c5965315-775d-45c8-ad9e-70e7ef29846e/61716d4d-6634-47d6-abf0-b43bb340aac1.jpg'],
                video_url: '#'
              },
              {
                id: 'v2',
                title: { da: 'Klientens 8. og sidste behandling' },
                description: { da: 'Hør om resultaterne efter 8. og sidste behandling i et metakognitivt forløb.' },
                thumbnail: ['https://assets.auxodev.dk/assets/c5965315-775d-45c8-ad9e-70e7ef29846e/61716d4d-6634-47d6-abf0-b43bb340aac1.jpg'],
                video_url: '#'
              }
            ]);
          }
        }
      } catch (error) {
        console.error('Error fetching videos:', error);
      } finally {
        setLoading(false);
      }
    };

    fetchVideos();
  }, []);

  if (loading) {
    return (
      <div className="min-h-screen bg-[#FAF7F2] flex items-center justify-center">
        <div className="animate-spin rounded-full h-12 w-12 border-t-2 border-b-2 border-[#123F13]"></div>
      </div>
    );
  }

  return (
    <div className="min-h-screen bg-[#FAF7F2]">
      <Helmet>
        <title>Se hvordan jeg arbejder – Videoer & Case Stories | Corehypnose</title>
        <meta name="description" content="Se video-anmeldelser og læs case stories om hvordan metakognitiv terapi og hypnose hjælper mod angst, stress og depression hos Lars Nissen Corell – Psykoterapeut MPF." />
      </Helmet>

      {/* Hero Section */}
      <section className="relative py-20 lg:py-32 overflow-hidden border-b border-[#E8DDCE] bg-white">
        <div className="container mx-auto px-4 relative z-10">
          <div className="max-w-4xl mx-auto text-center">
            <span className="inline-block px-4 py-1.5 mb-6 text-[11px] font-bold tracking-[0.2em] uppercase text-[#8C2F12] bg-[#8C2F12]/5">
              Resultater & Oplevelser
            </span>
            <h1 className="text-4xl md:text-5xl lg:text-6xl font-serif text-[#123F13] mb-8 leading-tight">
              Se hvordan jeg arbejder
            </h1>
            <p className="text-xl text-[#3B352F] font-light leading-relaxed max-w-2xl mx-auto">
              Her kan du høre tidligere klienter fortælle om deres rejse, se videoer fra forløb og læse om de konkrete forandringer, terapien skaber.
            </p>
          </div>

          {/* Case Story */}
          <div className="max-w-5xl mx-auto bg-white p-12 md:p-20 shadow-sm border border-[#E8DDCE] mt-16">
            <div className="grid lg:grid-cols-2 gap-16 items-center text-left">
              <div>
                <span className="text-[10px] font-bold tracking-[0.2em] uppercase text-[#8C2F12] mb-6 block">Case story</span>
                <h2 className="text-3xl font-serif text-[#123F13] mb-8 leading-tight">Mias historie: Fra kaos til klarhed</h2>
                <p className="text-[#3B352F]/80 leading-relaxed mb-8">
                  Mia, 45 år, lærer og mor til to, kom til mig med langvarig stress og følelsen af at miste sig selv.
                </p>
                <blockquote className="border-l-4 border-[#B89B5E] pl-8 py-2 italic text-lg text-[#123F13] mb-8 leading-relaxed">
                  "Jeg følte, jeg skulle være alt for alle. På arbejde, hjemme, i mit hoved. Jeg kunne ikke finde ro, og troede ærligt, at det bare var sådan, mit liv skulle være."
                </blockquote>
              </div>
              <div className="bg-[#FAF7F2] p-8 border border-[#E8DDCE]">
                <h4 className="font-serif text-[#123F13] mb-6">I forløbet hos mig fik Mia:</h4>
                <ul className="space-y-4 mb-8">
                  {[
                    "Klarhed over sine tankemønstre, og hvorfor hun blev ved med at overbelaste sig selv.",
                    "Redskaber til at håndtere uro og overtænkning, uden at skulle 'tale det hele igennem'.",
                    "Hypnose, der hjalp hende med at mærke sin krop og finde ro & nærvær indefra.",
                    "En konkret plan, der passede til hendes hverdag som mor og underviser."
                  ].map((item, i) => (
                    <li key={i} className="flex gap-3 text-sm text-[#3B352F]/80">
                      <CheckCircle2 className="w-5 h-5 text-[#8C2F12] shrink-0" />
                      <span>{item}</span>
                    </li>
                  ))}
                </ul>
                <div className="pt-6 border-t border-[#E8DDCE]">
                  <p className="text-[#123F13] font-serif italic">"For første gang i mange år føler jeg, at jeg kan trække vejret. Jeg er stadig mig, bare meget mere nærværende."</p>
                </div>
              </div>
            </div>
          </div>
        </div>
      </section>

      {/* Video Section */}
      <section className="py-24">
        <div className="container mx-auto px-4">
          <div className="max-w-4xl mx-auto text-center mb-16">
            <span className="inline-block px-4 py-1.5 mb-6 text-[11px] font-bold tracking-[0.2em] uppercase text-[#8C2F12] bg-[#8C2F12]/5">
              Video-anmeldelser
            </span>
            <h2 className="text-3xl md:text-4xl font-serif text-[#123F13] mb-6">Hør klienternes egne ord</h2>
            <p className="text-[#3B352F]/70">Oplev hvordan metakognitiv terapi har gjort en forskel for andre.</p>
          </div>

          {/* Video Grid */}
          <div className="grid md:grid-cols-2 gap-12 max-w-5xl mx-auto">
            {videos.map((video) => (
              <div 
                key={video.id} 
                className="group cursor-pointer"
                onClick={() => setActiveVideo(video)}
              >
                <div className="relative aspect-video bg-[#E8DDCE] overflow-hidden rounded-sm shadow-lg mb-6">
                  <img 
                    src={video.thumbnail?.[0]} 
                    alt={video.title.da} 
                    className="w-full h-full object-cover transition-transform duration-500 group-hover:scale-105"
                  />
                  <div className="absolute inset-0 bg-black/20 flex items-center justify-center group-hover:bg-black/30 transition-colors">
                    <div className="w-16 h-16 bg-white rounded-full flex items-center justify-center shadow-xl transform group-hover:scale-110 transition-transform">
                      <Play className="w-6 h-6 text-[#123F13] fill-[#123F13] ml-1" />
                    </div>
                  </div>
                </div>
                <h3 className="text-xl font-serif text-[#123F13] mb-2">{video.title.da}</h3>
                <p className="text-sm text-[#3B352F]/70 leading-relaxed">{video.description?.da}</p>
              </div>
            ))}
          </div>
        </div>
      </section>

      {/* Testimonials Section */}
      <TestimonialCarousel />

      {/* Video Modal */}
      {activeVideo && (
        <div className="fixed inset-0 z-[100] flex items-center justify-center p-4 bg-black/90 backdrop-blur-sm">
          <button 
            onClick={() => setActiveVideo(null)}
            className="absolute top-6 right-6 text-white hover:text-[#B89B5E] transition-colors"
          >
            <X className="w-8 h-8" />
          </button>
          <div className="w-full max-w-5xl aspect-video bg-black shadow-2xl">
            <iframe
              src={`https://www.youtube.com/embed/${activeVideo.video_url.includes('v=') ? activeVideo.video_url.split('v=')[1].split('&')[0] : activeVideo.video_url.split('/').pop().split('?')[0]}?autoplay=1`}
              title={activeVideo.title.da}
              className="w-full h-full"
              allow="accelerometer; autoplay; clipboard-write; encrypted-media; gyroscope; picture-in-picture"
              allowFullScreen
            ></iframe>
          </div>
        </div>
      )}

      {/* Bottom CTA */}
      <section className="py-24 border-t border-[#E8DDCE] bg-white">
        <div className="container mx-auto px-4 text-center">
          <h2 className="text-3xl font-serif text-[#123F13] mb-8">Vil du også opleve en forandring?</h2>
          <p className="text-[#3B352F]/70 max-w-xl mx-auto mb-12">
            Book en uforpligtende session og hør mere om, hvordan jeg kan hjælpe dig med at genvinde roen og overskuddet.
          </p>
          <div className="flex flex-col sm:flex-row gap-4 justify-center">
            <Link 
              to="/bestil-tid" 
              className="px-12 py-5 bg-[#123F13] text-white text-[12px] font-bold tracking-[0.2em] uppercase hover:bg-[#1a5a1c] transition-all shadow-lg"
            >
              Book første session
            </Link>
            <Link 
              to="/kontakt" 
              className="px-12 py-5 border border-[#123F13] text-[#123F13] text-[12px] font-bold tracking-[0.2em] uppercase hover:bg-[#FAF7F2] transition-all"
            >
              Kontakt mig
            </Link>
          </div>
        </div>
      </section>
    </div>
  );
};

export default SeHvordanJegArbejder;
